import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// eslint-disable-next-line spire/fenced-imports
import Plus from "@tIntermatic/Icons/Plus";
import parse from "html-react-parser";
import * as React from "react";
import { useLayoutEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}
type Props = OwnProps & HasProduct;

export interface ProductDetailsDescriptionStyles {
    wrapper?: InjectableCss;
    toggleIconProps?: IconPresentationProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    linkCss?: InjectableCss;
}

export const descriptionStyles: ProductDetailsDescriptionStyles = {
    wrapper: {
        css: css`
            margin-bottom: 15px;
        `,
    },
    specificationsAccordionSection: {
        toggleIconProps: {
            src: Minus,
            size: 15,
        },

        expandIconProps: {
            src: Plus,
            size: 15,
        },

        headerProps: {
            css: css`
                > button {
                    background: #fff;
                    border: none;
                    padding: 13px 9px 13px 15px !important;
                    > span {
                        font-size: 18px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 22px;
                        color: #333333;
                        text-transform: capitalize;
                    }
                }
            `,
        },

        panelProps: {
            css: css`
                border: none;
                padding-top: 0px;
                font-size: 16px;
                font-weight: 300;
                letter-spacing: 0;
                line-height: 24px;
                color: #33333a;
            `,
        },
    },

    linkCss: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 17px;
            color: #006bc0;
            margin-top: 17px;
            cursor: pointer;
        `,
    },
};

const styles = descriptionStyles;

const ProductDetailsDescription: React.FC<Props> = ({ product, fields }) => {
    if (!product.content) {
        return null;
    }

    if (
        (!product.content.htmlContent || product.content.htmlContent.length === 0) &&
        !(product.properties.isFamilySeries === "True" && product.properties.marketingDescription)
    ) {
        return null;
    }

    const [readMore, setReadMore] = useState(false);
    const stripedHtml =
        product.properties.isFamilySeries === "True" && product.properties.marketingDescription
            ? product.properties.marketingDescription
            : product.content.htmlContent.replace(/<[^>]+>/g, "");

    const characterCount = stripedHtml.trim().length;
    const [windowSize, setWindowSize] = React.useState(0);
    const [isMobile, setIsMobile] = React.useState(false);

    useLayoutEffect(() => {
        setWindowSize(window.innerWidth);
        if (windowSize < 768) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, [windowSize]);

    const fullDescription = (
        <StyledWrapper {...styles.wrapper}>
            <div data-test-selector="productDetails_htmlContent">{stripedHtml.trim()}</div>
        </StyledWrapper>
    );

    const shortDescription = (
        <StyledWrapper {...styles.wrapper}>
            <div data-test-selector="productDetails_htmlContent">
                {stripedHtml.trim().substring(0, 294)}
                {characterCount > 294 ? <span>...</span> : null}
            </div>
        </StyledWrapper>
    );

    const linkName = readMore ? translate("Read Less") : translate("Read More");

    return (
        <StyledWrapper>
            <Accordion
                headingLevel={1}
                css={css`
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                `}
            >
                {windowSize !== 0 && (
                    <AccordionSection
                        title={fields?.title}
                        expanded={!isMobile}
                        onTogglePanel={() => {
                            setIsMobile(!isMobile);
                        }}
                        {...styles.specificationsAccordionSection}
                    >
                        {readMore ? fullDescription : shortDescription}
                        {characterCount > 294 ? (
                            <StyledWrapper
                                onClick={() => {
                                    setReadMore(!readMore);
                                }}
                                {...styles.linkCss}
                            >
                                {linkName}
                            </StyledWrapper>
                        ) : null}
                    </AccordionSection>
                )}
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDescription),
    definition: {
        displayName: "Description",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Description",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
